import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import withAuth from '../../../HOC/withAuth';
import StepShell from '../StepShell';
import CopyNames from '../../../components/CopyNames';
import LocalSperater from '../../../components/LocalSperater';
import C from '../../../colors';
import { getPaymentStatus, isPaymentPaid } from '../../../orderStatus';
import http from '../../../http';
import { getPictureThumbnailUrl } from '../../../pictureUrls';
import { formatMoney } from '../../../money';
import { getPictureAccent } from '../../../selectionRecordColors';
import { buildSelectionPreviewPictures } from '../../../selectionPreview';
import ReadonlyImagePreview from '../../../components/ReadonlyImagePreview';

const CLIENT_COLORS = ['#F777B0','#D9883A','#6B8FB8','#3F8F5F','#8B6FB8','#D94F92','#C8782A','#4A7FA8'];

function isSelectionDeleteLocked(selection) {
  const status = getPaymentStatus(selection);
  return !!selection?.isPaid || status === 'requested' || status === 'paid';
}

// ── ClientSelectorStrip ───────────────────────────────────────────

function ClientSelectorStrip({ selections, pictures, activeClient, setActiveClient }) {
  // per-client counts
  const clientCounts = useMemo(() => {
    const m = {};
    selections.forEach(s => { m[s.id] = (s.pictureIds || []).length; });
    return m;
  }, [selections]);

  const consensusCount = useMemo(() => {
    const ids = new Set();
    selections.forEach(s => (s.pictureIds || []).forEach(pid => ids.add(pid)));
    return ids.size;
  }, [selections]);

  const isAggregate = activeClient === 'aggregate';

  return (
    <div style={{
      padding: '14px 36px',
      background: C.paper, borderBottom: `1px solid ${C.hair}`,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, color: C.ink3, textTransform: 'uppercase', fontWeight: 600, marginRight: 4 }}>
        选片人
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {selections.map((sel, i) => {
          const color = CLIENT_COLORS[i % CLIENT_COLORS.length];
          const active = !isAggregate && activeClient === sel.id;
          const count = clientCounts[sel.id] || 0;
          return (
            <button key={sel.id} data-testid={`selection-client-${sel.id}`} onClick={() => setActiveClient(sel.id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '4px 12px 4px 4px', borderRadius: 22,
              background: active ? '#fff' : 'transparent',
              border: `1px solid ${active ? color : C.hair2}`,
              boxShadow: active ? `0 0 0 2px ${color}26, 0 4px 14px -8px ${color}66` : 'none',
              color: C.ink, fontFamily: C.sans, cursor: 'pointer',
              transition: 'all .15s', whiteSpace: 'nowrap',
            }}>
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ width: 26, height: 26, borderRadius: 13, background: color, color: '#fff', display: 'grid', placeItems: 'center', fontFamily: C.serif, fontSize: 14, lineHeight: 1 }}>
                  {sel.customerName?.charAt(0) || '?'}
                </span>
                <span style={{ position: 'absolute', right: -1, bottom: -1, width: 8, height: 8, borderRadius: 4, background: isPaymentPaid(sel) ? '#3F8F5F' : C.ink3, border: '1.5px solid #fff' }}/>
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{sel.customerName}</span>
                <span style={{ fontFamily: C.mono, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: active ? color : C.ink3, fontWeight: active ? 600 : 500 }}>
                  {count}<span style={{ color: C.ink3, fontWeight: 400 }}>/{active ? '—' : ''}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <span style={{ width: 1, height: 24, background: C.hair2, margin: '0 4px' }}/>

      {/* 全员聚合 */}
      <button data-testid="selection-client-aggregate" onClick={() => setActiveClient('aggregate')} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '5px 12px 5px 8px', borderRadius: 22,
        background: isAggregate ? C.ink : '#fff',
        border: `1px solid ${isAggregate ? C.ink : C.hair2}`,
        color: isAggregate ? '#fff' : C.ink,
        fontFamily: C.sans, cursor: 'pointer', transition: 'all .15s',
      }}>
        <span style={{ display: 'inline-flex', position: 'relative', width: 20, height: 18 }}>
          {selections.slice(0, 3).map((s, i) => (
            <span key={s.id} style={{
              position: 'absolute', left: i * 5, top: 0,
              width: 14, height: 14, borderRadius: 7,
              background: CLIENT_COLORS[i % CLIENT_COLORS.length],
              border: `1.5px solid ${isAggregate ? C.ink : '#fff'}`,
            }}/>
          ))}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>全员聚合</span>
        <span style={{ fontFamily: C.mono, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: isAggregate ? 'rgba(255,255,255,.7)' : C.ink3 }}>
          {consensusCount}
        </span>
      </button>
    </div>
  );
}

// ── AggregateBadge ────────────────────────────────────────────────

function AggregateBadge({ selectedBy }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div onMouseEnter={() => setExpanded(true)} onMouseLeave={() => setExpanded(false)}
      style={{
        position: 'absolute', top: 6, right: 6,
        ...(expanded ? { bottom: 6 } : {}),
        display: 'flex', alignItems: expanded ? 'stretch' : 'center', gap: 6,
        padding: expanded ? '6px 10px' : '3px 7px 3px 5px',
        background: 'rgba(0,0,0,.55)', borderRadius: expanded ? 10 : 12,
        color: '#fff', zIndex: 8,
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        maxWidth: expanded ? '90%' : undefined, cursor: 'default',
      }}>
      {!expanded ? (
        <>
          <div style={{ display: 'flex', marginRight: 4 }}>
            {selectedBy.slice(0, 3).map((c, i) => (
              <span key={c.id} style={{
                width: 14, height: 14, borderRadius: 7,
                background: c.color, marginLeft: i === 0 ? 0 : -4,
                border: '1.5px solid rgba(0,0,0,.55)',
                display: 'grid', placeItems: 'center',
                fontFamily: C.serif, fontSize: 9, color: '#fff', lineHeight: 1,
              }}>{c.initial}</span>
            ))}
          </div>
          <span style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{selectedBy.length}</span>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxHeight: 180, overflowY: 'auto' }}>
          {selectedBy.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <span style={{ width: 14, height: 14, borderRadius: 7, flexShrink: 0, background: c.color, border: '1.5px solid rgba(255,255,255,.3)' }}/>
              <span style={{ fontSize: 11, fontFamily: C.sans, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
              {isPaymentPaid(c) && <span style={{ fontSize: 9, color: '#7FC99A', flexShrink: 0 }}>已付</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Step5 ──────────────────────────────────────────────────────────

const Step5 = inject('ordersStore', 'snackbarStore')(observer(({ ordersStore, snackbarStore }) => {
  const { orderId } = useParams();
  const [activeClient, setActiveClient] = useState('aggregate');
  const [deletingSelectionId, setDeletingSelectionId] = useState(null);
  const [previewPictureId, setPreviewPictureId] = useState(null);

  useEffect(() => { ordersStore.fetchOrder(orderId); }, [orderId]);

  const order = ordersStore.currentOrder || {};
  const photoTotal = order.Album?.pictureCount || 0;
  const photoSelected = order.selectedPhotoCount || 0;
  const selections = ordersStore.currentSelections || [];
  const pictures = ordersStore.currentPictures || [];
  const tagDefinitions = order.tagDefinitions || [];
  const tagById = useMemo(() => new Map(
    tagDefinitions.map(tag => [String(tag.id), tag])
  ), [tagDefinitions]);

  // Default to first client once selections load
  useEffect(() => {
    if (selections.length > 0 && activeClient === 'aggregate') {
      setActiveClient(selections[0].id);
    }
  }, [selections.length]);

  // Build selectedBy map
  const selectedByMap = useMemo(() => {
    const map = {};
    selections.forEach((sel, i) => {
      (sel.pictureIds || []).forEach(pid => {
        if (!map[pid]) map[pid] = [];
        map[pid].push({
          id: sel.id,
          name: sel.customerName,
          initial: sel.customerName?.charAt(0) || '?',
          color: CLIENT_COLORS[i % CLIENT_COLORS.length],
          paymentStatus: getPaymentStatus(sel),
        });
      });
    });
    return map;
  }, [selections]);

  const isAggregate = activeClient === 'aggregate';
  const activeSelection = selections.find(selection => selection.id === activeClient);

  const getPictureTags = (pictureId) => {
    if (!isAggregate) {
      const tagIds = activeSelection?.customerTagAssignments?.[String(pictureId)] || [];
      return tagIds.map(tagId => ({ tag: tagById.get(String(tagId)), count: 1 })).filter(item => item.tag);
    }
    const counts = new Map();
    selections.forEach(selection => {
      (selection.customerTagAssignments?.[String(pictureId)] || []).forEach(tagId => {
        const key = String(tagId);
        counts.set(key, (counts.get(key) || 0) + 1);
      });
    });
    return [...counts.entries()]
      .map(([tagId, count]) => ({ tag: tagById.get(tagId), count }))
      .filter(item => item.tag);
  };

  const handleDeleteSelection = async () => {
    if (!activeSelection || isSelectionDeleteLocked(activeSelection) || deletingSelectionId) return;
    const confirmed = window.confirm(`确定删除「${activeSelection.customerName}」的选图记录吗？删除后该客户可以重新提交。`);
    if (!confirmed) return;
    setDeletingSelectionId(activeSelection.id);
    try {
      await http.delete(`/user/selection/${activeSelection.id}`);
      setActiveClient('aggregate');
      await ordersStore.fetchOrder(orderId);
      snackbarStore.showSnackbar('选图记录已删除');
    } catch (error) {
      snackbarStore.showSnackbar('删除选图记录失败', 'error');
    } finally {
      setDeletingSelectionId(null);
    }
  };

  // Photos to display — aggregate shows ALL photos, individual shows all too

  if (!ordersStore.currentOrder) return <div style={{ padding: 40, color: C.ink3 }}>Loading...</div>;

  const aggregateCount = Object.keys(selectedByMap).length;

  // Pictures currently in view — used by CopyNames and LocalSperater
  const selectedPictures = buildSelectionPreviewPictures(pictures, selections, activeClient);

  return (
    <StepShell order={order} photoCount={photoTotal} photoSelected={photoSelected} photoTotal={photoTotal}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* ── Header ── */}
        <header style={{
          padding: '20px 36px',
          borderBottom: `1px solid ${C.hair}`,
          background: C.paper, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
        }}>
          <div>
            <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 4 }}>
              Step 05 · Records
            </div>
            <h2 data-testid="selection-record-heading" style={{ fontFamily: C.serif, fontSize: 28, lineHeight: 1.1, margin: 0, fontWeight: 400, color: C.ink, letterSpacing: -0.4 }}>
              {isAggregate ? '全员聚合' : (selections.find(s => s.id === activeClient)?.customerName || '')}
              <span style={{ color: C.ink3, fontSize: 20, marginLeft: 10 }}>
                · {isAggregate ? aggregateCount : (selections.find(s => s.id === activeClient)?.pictureIds?.length || 0)} 张已选
              </span>
            </h2>
            {!isAggregate && activeSelection && (
              <div data-testid="selection-record-summary" style={{ marginTop: 7, fontFamily: C.mono, fontSize: 11, color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>
                套餐 {activeSelection.photoCount || 0} 张
                <span style={{ margin: '0 7px', color: C.hair2 }}>·</span>
                加片 {Math.max(0, (activeSelection.pictureIds?.length || 0) - (activeSelection.photoCount || 0))} 张
                <span style={{ margin: '0 7px', color: C.hair2 }}>·</span>
                金额 ¥{formatMoney(activeSelection.totalPrice)}
              </div>
            )}
            {!isAggregate && activeSelection?.remark && (
              <div data-testid="selection-record-remark" style={{ marginTop: 7, fontSize: 12.5, color: C.ink2, lineHeight: 1.5 }}>
                客户备注：{activeSelection.remark}
              </div>
            )}
          </div>

          {selections.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {!isAggregate && <CopyNames pictures={selectedPictures}/>}
              <LocalSperater
                pictures={selectedPictures}
                dirname={isAggregate
                  ? '全员聚合'
                  : selections.find(s => s.id === activeClient)?.customerName}
              />
              {!isAggregate && (!isSelectionDeleteLocked(activeSelection) ? (
                <button
                  type="button"
                  data-testid={`delete-selection-${activeSelection?.id}`}
                  disabled={deletingSelectionId === activeSelection?.id}
                  onClick={handleDeleteSelection}
                  style={{
                    padding: '7px 11px', borderRadius: 7, border: '1px solid #e8b5ae',
                    background: '#fff', color: '#A53636', fontFamily: C.sans, fontSize: 12,
                    cursor: deletingSelectionId ? 'wait' : 'pointer',
                    opacity: deletingSelectionId ? 0.6 : 1,
                  }}
                >
                  {deletingSelectionId === activeSelection?.id ? '删除中…' : '删除记录'}
                </button>
              ) : (
                <span data-testid="selection-delete-locked" style={{ fontSize: 11, color: C.ink3 }}>
                  {getPaymentStatus(activeSelection) === 'requested' ? '已请求支付，不能删除' : '已支付记录不可删除'}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* ── Client selector ── */}
        {selections.length > 0 && (
          <ClientSelectorStrip
            selections={selections}
            pictures={pictures}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
          />
        )}

        {/* ── Grid ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 36px 64px' }}>
          {selections.length === 0 ? (
            <div style={{ padding: '80px 0', textAlign: 'center', color: C.ink3, fontSize: 14 }}>暂无客户选图</div>
          ) : (
            pictures.length === 0 ? (
              <div style={{ padding: '80px 0', textAlign: 'center', color: C.ink3, fontSize: 14 }}>
                {isAggregate ? '暂无共同选图' : '该客户未选任何照片'}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                {pictures.map(pic => {
                  const selectedBy = selectedByMap[pic.id] || [];
                  const isSelected = isAggregate ? selectedBy.length > 0 : selectedBy.some(c => c.id === activeClient);
                  const accent = getPictureAccent(selectedBy, activeClient, isAggregate, C.brand);
                  const pictureTags = getPictureTags(pic.id);
                  return (
                    <div
                      key={pic.id}
                      data-testid={`selection-record-photo-${pic.id}`}
                      data-selected={isSelected ? 'true' : 'false'}
                      data-selected-count={selectedBy.length}
                      role={isSelected ? 'button' : undefined}
                      tabIndex={isSelected ? 0 : undefined}
                      aria-label={isSelected ? `预览照片 ${pic.originalFileName || pic.fileName || ''}` : undefined}
                      onClick={isSelected ? () => setPreviewPictureId(pic.id) : undefined}
                      onKeyDown={isSelected ? event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setPreviewPictureId(pic.id);
                        }
                      } : undefined}
                      style={{
                      position: 'relative', aspectRatio: '4/3', borderRadius: 4, overflow: 'hidden', background: C.paper2,
                      opacity: isSelected ? 1 : 0.35,
                      boxShadow: isSelected ? `0 0 0 3px ${accent}, 0 6px 18px -8px ${accent}88` : '0 1px 2px rgba(0,0,0,.06)',
                      transition: 'all .2s', cursor: isSelected ? 'zoom-in' : 'default', outlineOffset: 3,
                    }}>
                      <img src={getPictureThumbnailUrl(pic)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>
                      <div style={{ position: 'absolute', inset: 0, background: isSelected ? 'radial-gradient(ellipse at 35% 30%, rgba(255,255,255,.2), transparent 55%)' : 'radial-gradient(ellipse at 35% 30%, rgba(255,255,255,.05), transparent 55%)', pointerEvents: 'none' }}/>
                      {isAggregate && selectedBy.length > 0 && <AggregateBadge selectedBy={selectedBy}/>}
                      {pictureTags.length > 0 && (
                        <div data-testid={`selection-record-tags-${pic.id}`} style={{
                          position: 'absolute', left: 6, top: 6, zIndex: 9,
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
                        }}>
                          {pictureTags.map(({ tag, count }) => (
                            <span key={tag.id} data-tag-id={tag.id} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 6px', borderRadius: 10,
                              background: 'rgba(20,18,17,.68)', color: '#fff',
                              backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
                              fontFamily: C.sans, fontSize: 10, lineHeight: 1,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: 3, background: tag.color || C.brand }}/>
                              {tag.name}{isAggregate && count > 1 ? ` ×${count}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '18px 7px 5px', background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,.68))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,.85)', fontFamily: 'ui-monospace,monospace', letterSpacing: 0.2 }}>
                        {pic.originalFileName || pic.fileName}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        <ReadonlyImagePreview
          pictures={selectedPictures}
          initialPictureId={previewPictureId}
          onClose={() => setPreviewPictureId(null)}
        />

      </div>
    </StepShell>
  );
}));

export default withAuth(Step5);
